import type { RegistrationSchemaType } from './stepsMap';
import type { NextRouter } from 'next/router';

import { useRouter } from 'next/router';
import { parse } from 'query-string';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import stepConfig from './stepConfig';

import theme from '$/assets/theme';
import useElb, { ElbProps } from '$/hooks/useElb';
import { HeaderContext } from '$/providers/HeaderProvider';
import stateObjectToJson from '$/utils/stateObjectToJson';

export interface IQuestionPageContext {
  state: RegistrationSchemaType;
  currentStep: keyof typeof stepConfig;
  setCurrentStep: (
    data: keyof typeof stepConfig,
    delay?: number,
    queryParameters?: { [key: string]: string }
  ) => void;
  dispatch: React.Dispatch<Action>;
  background: string;
  clearPersist: () => void;
}

export const QuestionPageContext = createContext<IQuestionPageContext>({
  state: {},
  currentStep: 'major',
  setCurrentStep: () => null,
  dispatch: () => null,
  background: theme.colors.white,
  clearPersist: () => null,
});

type Action = { type: 'set'; payload: Partial<RegistrationSchemaType> };

const LOCAL_STORAGE_KEY = 'registration';

function reducer(state: RegistrationSchemaType, action: Action) {
  switch (action.type) {
    case 'set':
      const newState = {
        ...state,
        ...action.payload,
      };

      window?.sessionStorage.setItem(
        LOCAL_STORAGE_KEY,
        stateObjectToJson(newState)
      );

      return newState;
    default:
      throw new Error();
  }
}

let initialState = {};

if (typeof window !== 'undefined') {
  initialState =
    JSON.parse(window?.sessionStorage.getItem(LOCAL_STORAGE_KEY)) || {};
}

const getQuery = (router: NextRouter) => {
  return parse(router.asPath.substr(router.asPath.indexOf('?') + 1));
};

const QuestionPageProvider: React.FC = (props) => {
  const { setProgressBarValue } = useContext(HeaderContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [background, setBackground] = useState(theme.colors.white);
  const router = useRouter();

  const setCurrentStep = (
    newStep: keyof typeof stepConfig,
    delay = 200,
    queryParameters = {}
  ): void => {
    // Interaction was too fast, we had to invest in a setTimeout to slow it down
    setTimeout(() => {
      return router.push({
        pathname: router.pathname,
        query: {
          ...queryParameters,
          step: newStep,
        },
      });
    }, delay);
  };

  // ignored because of TS limitation
  // @ts-ignore
  const currentStep = useMemo<keyof typeof stepConfig>(() => {
    const step = getQuery(router).step as string;

    if (!stepConfig[step]) {
      return 'major';
    } else {
      return step;
    }
  }, [router.query]);

  useEffect(() => {
    const step = stepConfig[currentStep];

    window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    setProgressBarValue(step.progressBarValue);
    setBackground(step.background || theme.colors.white);
  }, [currentStep]);

  const clearPersist = () =>
    window?.sessionStorage.removeItem(LOCAL_STORAGE_KEY);

  return (
    <QuestionPageContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        state,
        dispatch,
        background,
        clearPersist,
      }}
    >
      {props.children}
    </QuestionPageContext.Provider>
  );
};

export default QuestionPageProvider;
